import {
  ChangeDetectorRef,
  Component,
  Injector,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  SiaBaseScriptComponent,
  SiaComponentConfigPerspectives,
  SiaComponentConfigService,
  SiaComponentService,
  SiaLayoutService,
  SiaMenu,
  SiaMenuService,
  SiaRouteData,
  SiaTranslatePipe,
  YesNoDialogComponent,
  jsonDeepClone,
  notify,
  retriveCurrentRouteData,
} from '@sia/shared';
import { Subscription, firstValueFrom } from 'rxjs';
import { MatEditPerspectiveDialogComponent } from '../../dialogs/mat-edit-perspective-dialog';
import { NewPerspectiveDialogComponent } from '../../dialogs/new-grid-perspective-dialog';

import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { SiaAction, SiaActionFired, SiaActionsService } from '@sia/actions';
import { SiaSelectBoxComponent } from '@sia/inputs';
import { SiaTemplateConfiguratorComponent } from '@sia/template';
import { SiaMatGridConfiguratorColumnsComponent } from '../../components/sia-mat-grid-configurator-columns/sia-mat-grid-configurator-columns.component';
import { SiaMatGridConfiguratorGeneralComponent } from '../../components/sia-mat-grid-configurator-general/sia-mat-grid-configurator-general.component';
import { SiaGridConfig } from '../../interfaces/sia-grid-config';
import { GridConfigService } from '../../services/grid-config.service';

@Component({
  selector: 'sia-mat-grid-configurator-page',
  templateUrl: './sia-mat-grid-configurator-page.component.html',
  styleUrls: ['./sia-mat-grid-configurator-page.component.scss'],
  standalone: true,
  imports: [
    SiaMatGridConfiguratorColumnsComponent,
    SiaMatGridConfiguratorGeneralComponent,
    SiaTemplateConfiguratorComponent,
    SiaSelectBoxComponent,
    SiaTranslatePipe,
    MatTabGroup,
    MatButton,
    MatIcon,
    MatTab,
  ],
})
export class SiaMatGridConfiguratorPageComponent extends SiaBaseScriptComponent {
  #siaComponentConfigService = inject(SiaComponentConfigService);
  #siaComponentService = inject(SiaComponentService);
  #gridConfigService = inject(GridConfigService);
  #siaActionsService = inject(SiaActionsService);
  #siaLayoutService = inject(SiaLayoutService);
  #siaMenuService = inject(SiaMenuService);
  #cdr = inject(ChangeDetectorRef);
  #matDialog = inject(MatDialog);
  #injector = inject(Injector);

  #selectedPerspective?: SiaComponentConfigPerspectives;
  #selectedPerspectiveId?: string | null;
  #subs: Subscription[] = [];
  #pageData?: SiaRouteData;
  #gridId?: string;

  dataBaseFields?: { id: string; description: string }[];
  menu?: SiaMenu | null;
  perspectives: SiaComponentConfigPerspectives[] = [];
  gridConfiguration?: SiaGridConfig | null;

  get selectedPerspectiveId(): string | null | undefined {
    return this.#selectedPerspectiveId;
  }
  set selectedPerspectiveId(value: string | null | undefined) {
    this.#selectedPerspectiveId = value;
    this.#selectedPerspective = this.perspectives.find((x) => x.id == value);
    if (!value) {
      this.gridConfiguration = null;
      this.siaBaseActionService.disableAction(
        this.baseRouterPageData.componentId ?? '',
        this.baseRouterPageData.guid ?? '',
        'save',
        true,
      );
      this.siaBaseActionService.disableAction(
        this.baseRouterPageData.componentId ?? '',
        this.baseRouterPageData.guid ?? '',
        'delete',
        true,
      );
      this.siaBaseActionService.disableAction(
        this.baseRouterPageData.componentId ?? '',
        this.baseRouterPageData.guid ?? '',
        'update',
        true,
      );
      return;
    }
    this.gridConfiguration = JSON.parse(
      this.perspectives.find((p) => p.id === value)?.jsonConfig ?? '{}',
    );
    this.ensureGroupConfiguration();
    if (this.gridConfiguration && !this.gridConfiguration.template) {
      this.gridConfiguration!.template = {
        viewName: this.menu?.viewName ?? this.menu?.tableName,
        viewSchema: this.menu?.viewSchema ?? this.menu?.tableSchema,
      };
    }
    if (this.gridConfiguration && !this.gridConfiguration.group) {
      this.gridConfiguration!.group = {};
    }
    this.gridConfiguration!.template = jsonDeepClone(
      this.gridConfiguration?.template,
    );
    if (
      this.gridConfiguration?.template &&
      (!this.gridConfiguration?.template.viewName ||
        !this.gridConfiguration?.template.viewSchema)
    ) {
      this.gridConfiguration!.template.viewName = this.menu?.viewName;
      this.gridConfiguration!.template.viewSchema = this.menu?.viewSchema;
    }

    if (
      this.gridConfiguration?.template &&
      !this.gridConfiguration?.template.connectionStringName
    ) {
      this.gridConfiguration!.template.connectionStringName =
        this.menu?.connectionName;
    }
    this.siaBaseActionService.disableAction(
      this.siaRouterService.getLatestRoutingData()?.componentId ?? '',
      this.siaRouterService.getLatestRoutingData()?.guid ?? '',
      'save',
      false,
    );
    this.siaBaseActionService.disableAction(
      this.siaRouterService.getLatestRoutingData()?.componentId ?? '',
      this.siaRouterService.getLatestRoutingData()?.guid ?? '',
      'delete',
      false,
    );
    this.siaBaseActionService.disableAction(
      this.siaRouterService.getLatestRoutingData()?.componentId ?? '',
      this.siaRouterService.getLatestRoutingData()?.guid ?? '',
      'update',
      false,
    );
  }

  private ensureGroupConfiguration() {
    if (!this.gridConfiguration) return;

    if (!this.gridConfiguration.group) {
      this.gridConfiguration.group = {};
    }
    if (
      this.gridConfiguration.groupDataField &&
      !this.gridConfiguration.group.groupDataField
    ) {
      this.gridConfiguration.group.groupDataField =
        this.gridConfiguration.groupDataField;
    }
    if (
      this.gridConfiguration.groupNameDataField &&
      !this.gridConfiguration.group.groupNameDataField
    ) {
      this.gridConfiguration.group.groupNameDataField =
        this.gridConfiguration.groupNameDataField;
    }
    if (
      this.gridConfiguration.groupText &&
      !this.gridConfiguration.group.groupText
    ) {
      this.gridConfiguration.group.groupText = this.gridConfiguration.groupText;
    }
  }

  constructor() {
    super();
    this.#gridId = this.siaSessionService.getSessionParamValue('gridId');
    if (!this.#gridId) {
      this.siaRouterService.navigateBack();
    }
  }
  async loadGridInformations() {
    if (!this.#gridId) return;
    this.menu = await this.#siaMenuService.getMenu(this.#gridId);
    const viewInfo = await this.#siaComponentService.getViewInformations(
      this.#gridId ?? '',
    );
    this.dataBaseFields = [];
    this.dataBaseFields = viewInfo.map((x) => {
      if (x.columnName == null) return { id: '', description: '' };
      return { id: x.columnName?.toLowerCase(), description: x.columnName };
    });
    this.#siaLayoutService.showAdditionalTitle(
      this.menu?.title ?? '',
      this.siaRouterService.getLatestRoutingData()?.guid ?? '',
    );
    this.perspectives =
      (await this.#siaComponentConfigService.getComponentPerspectives(
        'grid',
        this.#gridId,
      )) ?? [];
    this.selectedPerspectiveId = this.perspectives
      ? (this.perspectives[0]?.id ?? null)
      : null;
  }
  createPerspective() {
    const dialogRef = this.#matDialog.open(NewPerspectiveDialogComponent, {
      width: '500px',
      data: {},
    });

    let s0 = dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) return;
      const newPerspective: SiaComponentConfigPerspectives = {
        componentId: this.menu?.id,
        configType: 'grid',
        description: result.name,
        defaultMode: result.defaultMode,
      };

      let defaultConfig =
        await this.#gridConfigService.getGridDefaultConfiguration(
          'MASTER',
          this.menu?.id ?? '',
        );
      newPerspective.jsonConfig = JSON.stringify(defaultConfig);
      this.#siaComponentConfigService
        .setComponentPerspective(newPerspective)
        .then(async (data: SiaComponentConfigPerspectives) => {
          this.perspectives =
            (await this.#siaComponentConfigService.getComponentPerspectives(
              'grid',
              this.menu?.id ?? '',
            )) ?? [];
          this.#cdr.detectChanges();
          this.selectedPerspectiveId = data.id;
        });
    });
    this.#subs.push(s0);
  }
  async deleteSelectedComponentConfigPerspective() {
    if (!this.#selectedPerspectiveId) return;
    var dialogRef = this.#matDialog.open(YesNoDialogComponent, {
      width: '350px',
      data: { title: 'Eliminare il record corrente?' },
    });
    let proceedDelete = await firstValueFrom(dialogRef.afterClosed());
    if (!proceedDelete) return;
    this.#siaComponentConfigService
      .deletePerspective(this.#selectedPerspectiveId ?? '')
      .then(async (result) => {
        if (!result) {
          this.notify('Prospettiva non eliminata', 2000, 'warning');
          return;
        }
        notify('Eliminata con successo', 2000, 'success');
        let indexToRemove = this.perspectives.findIndex(
          (x) => x.id === this.#selectedPerspectiveId,
        );
        if (indexToRemove != -1) {
          this.perspectives.splice(indexToRemove, 1);
        }
        this.perspectives =
          (await this.#siaComponentConfigService.getComponentPerspectives(
            'grid',
            this.menu?.id ?? '',
          )) ?? [];
        this.selectedPerspectiveId = null;
      })
      .catch((err) => {
        notify('Impossibile eliminare prospettiva', 2000, 'error');
      });
  }
  saveSelectedComponentConfigPerspective() {
    if (!this.#selectedPerspective || !this.gridConfiguration) {
      return;
    }
    let newConfiguration: SiaGridConfig = jsonDeepClone(this.gridConfiguration);
    if (
      !newConfiguration.template ||
      JSON.stringify(newConfiguration.template) == '{}'
    ) {
      newConfiguration.showTemplate = undefined;
    }
    newConfiguration.cols?.map((x) => {
      x.configurationVisible = false;
      return x;
    });

    this.#selectedPerspective.jsonConfig = JSON.stringify(newConfiguration);
    this.#siaComponentConfigService
      .updateComponentPerspective(this.#selectedPerspective ?? {})
      .then((res) => {
        if (res) {
          notify('Salvata con successo', 2000, 'success');
          this.siaSessionService.weakMemoryClear(this.menu?.id + '-config');
          this.siaSessionService.clear(this.menu?.id + '-columns');
          this.#selectedPerspective = jsonDeepClone(res);
        }
      });
  }
  async editSelectedComponentConfigPerspective() {
    if (!this.#selectedPerspective || !this.gridConfiguration) {
      return;
    }
    this.#selectedPerspective.jsonConfig = JSON.stringify(
      this.gridConfiguration,
    );

    this.#selectedPerspective = jsonDeepClone(this.#selectedPerspective);
    const dialogRef = this.#matDialog.open(MatEditPerspectiveDialogComponent, {
      width: '500px',
      data: this.#selectedPerspective,
    });

    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res && this.#selectedPerspective) {
      this.#selectedPerspective = jsonDeepClone(res);
      await this.#siaComponentConfigService.updateComponentPerspective(
        this.#selectedPerspective ?? {},
      );
      this.siaSessionService.weakMemoryClear(this.menu?.id + '-config');
      this.siaSessionService.clear(this.menu?.id + '-columns');
      this.perspectives =
        (await this.#siaComponentConfigService.getComponentPerspectives(
          'grid',
          this.menu?.id ?? '',
        )) ?? [];
      this.notify('Salvata con successo', 2000, 'success');
    }
  }

  async manageActionFired(action: SiaAction, element?: MatMenuTrigger) {
    switch (action.id) {
      case 'save':
        this.saveSelectedComponentConfigPerspective();
        break;
      case 'delete':
        this.deleteSelectedComponentConfigPerspective();
        break;
      case 'update':
        await this.editSelectedComponentConfigPerspective();
        break;
      case 'editsql':
        this.editSql();
        break;
      case 'create':
        this.createPerspective();
        break;
      case 'refresh':
        this.refreshDataFields();
        break;
      case 'download-perspective':
        this.fileService.downloadPerspective(this.#gridId || '', 'grid');
        break;
    }
  }

  async refreshDataFields() {
    if (!this.gridConfiguration) {
      return;
    }
    if (!this.gridConfiguration?.cols) {
      this.gridConfiguration!.cols = [];
    }
    const viewInfo = await this.#siaComponentService.getViewInformations(
      this.menu?.id ?? '',
    );
    this.dataBaseFields = viewInfo.map((x) => {
      if (!x.columnName) return { id: '', description: '' };
      return { id: x.columnName?.toLowerCase(), description: x.columnName };
    });

    const newFields = this.dataBaseFields.filter(
      (field) =>
        !this.gridConfiguration?.cols?.some(
          (col) => col.dataField?.toLowerCase() === field.id.toLowerCase(),
        ),
    );

    newFields.forEach((field) => {
      if (field.id || field.description) {
        this.gridConfiguration?.cols?.unshift({
          dataField: field.id,
          caption: field.description,
          dataType: 'string',
          visible: true,
          sortOrder: 0,
          visibleIndex: 0,
          allowSorting: true,
          allowFilter: true,
          selected: true,
        });
      }
    });

    this.gridConfiguration = jsonDeepClone(this.gridConfiguration);

    this.notify('Aggiornati i campi del database', 2000, 'success');
  }
  applyTemplateConfiguration($event: any) {
    if (!this.gridConfiguration) {
      return;
    }
    this.gridConfiguration.template = $event;
  }

  editSql() {
    this.siaRouterService.navigateToSqlEditor(this.menu?.id ?? '');
  }

  async ngOnInit() {
    super.ngOnInit();

    let s = this.siaRouterService.routeChanged$.subscribe(() => {
      var pageData: any = null;
      runInInjectionContext(this.#injector, () => {
        pageData = retriveCurrentRouteData() ?? {};
      });
      if (!pageData?.guid || pageData?.guid == this.#pageData?.guid) {
        return;
      }
      this.#pageData = pageData!;
      if (this.#pageData) {
        this.siaBaseActionService.handleAction(
          this.baseRouterPageData.componentId ?? '',
          this.baseRouterPageData.guid ?? '',
          'import',
          false,
        );
      }
    });
    this.#subs.push(s);

    await this.loadGridInformations();

    let s0 = this.#siaActionsService
      .actionFired$()
      .subscribe((action: SiaActionFired) => {
        if (action.action) {
          this.manageActionFired(action.action, action.actionTrigger);
        }
      });
    this.#subs.push(s0);
  }

  importAction() {
    this.#siaActionsService.fireSiaAction({
      guid: this.baseRouterPageData.guid,
      componentId: this.baseRouterPageData.componentId,
      action: {
        id: 'import',
      },
    });
  }

  ngOnDestroy() {
    this.#subs.forEach((s) => s.unsubscribe());
    super.ngOnDestroy();
  }
}
